import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { DashboardEditorProps } from "components/ComponentTypeTypes";
import { Divider } from "primereact/divider";
import DatasetMultiSelect from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import React, { FC, useEffect, useState } from "react";
import { defaultLoadConf } from "components/dashboard/data-input-output-table/defaultConfigs";
import { T } from "services/lang/LanguageService";
import LoadConfigInputs from "components/dashboard/charts/LoadConfigInputs";
import { DatasetLoadingConf } from "components/dashboard/charts/interfaces";
import {
  aggregationPeriodOptions,
  DataDisplayType,
  dataDisplayTypeOptions,
  diffTypeOptions,
  ValueChangeIndicatorProps,
} from "../ValueChangeIndicatorTypes";

const ValueChangeIndicatorEditor: FC<DashboardEditorProps<ValueChangeIndicatorProps>> = ({
  config,
  onConfigChange,
}) => {
  const [currentConfig, setCurrentConfig] = useState<ValueChangeIndicatorProps>({
    loadConf: defaultLoadConf,
    ...config,
  });

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig]);

  const handleDatasetChange = (value: string | null | string[], index: number) => {
    const updated = [...(currentConfig.datasets ?? [])];

    if (value === null) {
      updated.splice(index, 1);
    } else if (Array.isArray(value)) {
      const primary = updated[0] ?? "";
      updated.length = 0;
      updated.push(primary, ...value);
    } else {
      updated[index] = value;
    }

    setCurrentConfig({ ...currentConfig, datasets: updated });
  };

  const handleMultiDatasetChange = (value: string[]) => {
    const primary = currentConfig.datasets[0] ?? "";
    const uniqueValues = [...new Set(value.filter(v => v !== primary))];
    setCurrentConfig({ ...currentConfig, datasets: [primary, ...uniqueValues] });
  };

  const handleDisplayLastValueDiffChange = (value: boolean) => {
    if (value) {
      setCurrentConfig({
        ...currentConfig,
        displayLastValueDiff: value,
        datasets: [currentConfig.datasets[0]],
      });
      return;
    }
    setCurrentConfig({ ...currentConfig, displayLastValueDiff: value });
  };

  const getDatasetSelectionInput = () => {
    if (currentConfig.displayInline) {
      return (
        <div className="flex flex-column gap-2 w-8">
          <label htmlFor="valueDataset">{T("word.dataset")}</label>
          <DatasetDropdown
            value={currentConfig.datasets?.[1] ?? ""}
            onChange={val => handleDatasetChange(val, 1)}
          />
        </div>
      );
    }
    return (
      <div className="flex flex-column gap-2 w-8">
        <label htmlFor="valueDataset">{T("word.datasets")}</label>
        <DatasetMultiSelect
          value={currentConfig.datasets.slice(1) ?? []}
          onChange={handleMultiDatasetChange}
        />
      </div>
    );
  };

  return (
    <div className="flex flex-column">
      <div className="flex flex-row gap-3">
        <div className="flex flex-column gap-2 w-8 mb-3">
          <label htmlFor="valueDataset">{T("word.dataset")}</label>
          <DatasetDropdown
            value={currentConfig.datasets?.[0] ?? ""}
            onChange={val => handleDatasetChange(val, 0)}
          />
        </div>
        <div className="flex flex-column w-4 gap-2">
          <label htmlFor="title">{T("word.title")}</label>
          <InputText
            id="title"
            value={currentConfig.title}
            onChange={e => setCurrentConfig({ ...currentConfig, title: e.target.value })}
            className="p-inputtext-sm"
            placeholder={T("action.write-title")}
          />
        </div>
      </div>
      <div className="flex flex-row gap-3">
        <div className="flex flex-column gap-2">
          <label htmlFor="subtitle">{T("word.subtitle")}</label>
          <InputText
            id="subtitle"
            value={currentConfig.subtitle}
            onChange={e => setCurrentConfig({ ...currentConfig, subtitle: e.target.value })}
            className="p-inputtext-sm"
            placeholder={T("action.write-subtitle")}
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="dataDisplayType">{T("label.data-display-type")}</label>
          <Dropdown
            id="dataDisplayType"
            value={currentConfig.dataDisplayType ?? DataDisplayType.LAST_VALUE}
            options={dataDisplayTypeOptions}
            onChange={e => setCurrentConfig({ ...currentConfig, dataDisplayType: e.value })}
          />
        </div>
        {currentConfig.dataDisplayType !== DataDisplayType.HORIZONTAL_BAR_CHART && (
          <div className="flex flex-column gap-2 w-2">
            <label htmlFor="highlightValue">{T("action.highlight-value")}</label>
            <InputSwitch
              id="highlightValue"
              checked={currentConfig.highlightValue ?? false}
              onChange={e => setCurrentConfig({ ...currentConfig, highlightValue: e.value })}
            />
          </div>
        )}
        {currentConfig.dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART && (
          <div className="flex flex-column gap-2">
            <label htmlFor="aggregationPeriod">{T("label.aggregation-period")}</label>
            <Dropdown
              id="aggregationPeriod"
              value={currentConfig.aggregationPeriod ?? ""}
              options={aggregationPeriodOptions}
              onChange={e => setCurrentConfig({ ...currentConfig, aggregationPeriod: e.value })}
            />
          </div>
        )}
        <div className="w-2 gap-2 flex flex-column justify-content-between">
          <label htmlFor="updateFrequency">{T("load-conf.update-frequency")}</label>
          <div className="p-inputgroup w-full">
            <InputNumber
              id="updateFrequency"
              value={currentConfig.updateFrequency as number}
              onValueChange={e =>
                setCurrentConfig({ ...currentConfig, updateFrequency: e.value ?? 0 })
              }
            />
            <span className="p-inputgroup-addon">s</span>
          </div>
        </div>
        {!currentConfig.displayLastValueDiff &&
          currentConfig.dataDisplayType !== DataDisplayType.HORIZONTAL_BAR_CHART && (
            <div className="flex flex-column gap-2">
              <label htmlFor="displayInline">{T("label.display-inline")}</label>
              <InputSwitch
                id="displayInline"
                checked={currentConfig.displayInline ?? false}
                onChange={e => setCurrentConfig({ ...currentConfig, displayInline: e.value })}
              />
            </div>
          )}

            <div className="flex flex-column gap-2">
              <label htmlFor="hideUnit">{T("action.hide-unit")}</label>
              <InputSwitch
                id="hideUnit"
                checked={currentConfig.hideUnit ?? false}
                onChange={e => setCurrentConfig({ ...currentConfig, hideUnit: e.value })}
              />
            </div>
          
      </div>
      <Divider />
      <div className="flex flex-row gap-3 justify-content-between">
        <div className="flex flex-column gap-2 w-4">
          <label htmlFor="displayLastValueDiff">{T("action.display-last-value-difference")}</label>
          <InputSwitch
            id="displayLastValueDiff"
            checked={currentConfig.displayLastValueDiff ?? false}
            onChange={e => handleDisplayLastValueDiffChange(e.value)}
          />
        </div>
        {!currentConfig.displayLastValueDiff &&
        currentConfig.dataDisplayType !== DataDisplayType.HORIZONTAL_BAR_CHART ? (
          <>{getDatasetSelectionInput()}</>
        ) : (
          <>
            <div className="flex flex-column gap-2 w-3">
              <label htmlFor="useDiffIndicatorColors">
                {T("action.use-diff-indicator-colors")}
              </label>
              <InputSwitch
                id="useDiffIndicatorColors"
                checked={currentConfig.useDiffIndicatorColors ?? false}
                onChange={e =>
                  setCurrentConfig({ ...currentConfig, useDiffIndicatorColors: e.value })
                }
              />
            </div>
            <div className="flex flex-column gap-2 w-2">
              <label htmlFor="diffType">{T("word.diff-type")}</label>
              <Dropdown
                id="diffType"
                value={currentConfig.diffType}
                options={diffTypeOptions}
                onChange={e => setCurrentConfig({ ...currentConfig, diffType: e.value })}
                placeholder={T("word.select-one")}
                className="p-inputtext-sm"
              />
            </div>
            <div className="flex flex-column gap-2 w-3">
              <label htmlFor="diffValueDecimalPlaces">{T("word.diff-decimal-places")}</label>
              <InputNumber
                id="diffValueDecimalPlaces"
                value={currentConfig.diffValueDecimalPlaces as number}
                onValueChange={e =>
                  setCurrentConfig({ ...currentConfig, diffValueDecimalPlaces: e.value ?? 0 })
                }
              />
            </div>
          </>
        )}
      </div>
      <Divider />
      {currentConfig.dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART && (
        <div className="flex flex-row gap-2">
          <LoadConfigInputs
            loadConf={currentConfig.loadConf as DatasetLoadingConf}
            handleLoadConfChange={e =>
              setCurrentConfig(prev => ({
                ...prev,
                loadConf: e,
              }))
            }
          />
        </div>
      )}
    </div>
  );
};

export default ValueChangeIndicatorEditor;
